import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import { renderSlot } from 'vue';
import { Vue, Options } from 'vue-class-component';

import { IRepository } from '@/types/Repository';
import { RouteRecordName } from 'vue-router';

class Props<E extends Entity> {
  repository!: IRepository<E>;
}

type Emits<E> = {
  onRefresh?: (entity: E) => void;
};

@Options({
  emits: ['refresh'],
  watch: {
    entityId: {
      immediate: true,
      handler(this: EntityLoader<Entity>) {
        if (!!this.routeName && this.$route.name !== this.routeName) {
          return;
        }

        if (this.entityId) {
          this.load();
        }
      },
    },
  },
})
export default class EntityLoader<E extends Entity> extends Vue.with(Props) {
  declare $props: $Props<Props<E>, Emits<E>>;
  private loading = false;
  private entity: Nullable<Entity> = null;
  private routeName: Nullable<RouteRecordName> = null;

  get entityId(): string {
    const entityId = this.$route.params.id;
    return Array.isArray(entityId) ? entityId[0] : entityId;
  }

  public changeEntity(entity: E) {
    this.entity = entity;
  }

  async load() {
    const { entityId } = this;
    if (!entityId) {
      return;
    }

    this.loading = true;
    try {
      const entity = await this.repository.get(entityId);
      this.entity = entity;
      this.$emit('refresh', entity);
    } catch (e) {
      this.$processException(e);
    }
    this.loading = false;
  }

  render() {
    const { loading, entity, $slots } = this;
    if (loading) {
      return <spinner />;
    }
    return renderSlot($slots, 'default', { entity });
  }

  mounted() {
    if (this.$route.name) {
      this.routeName = this.$route.name;
    }
  }
}
